import { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { extractErrorMessage, useStore } from "../../../../helpers";
import {
  extractProjectQuestionKeysFromSource,
  isEsIndexNotFoundError,
  useEsSampleDoc,
} from "../../tars-hooks/useEsSampleDoc";

const EsSampleKeysSync = observer(function EsSampleKeysSync() {
  const { tarsStore, tarsAnalyticsStore } = useStore();
  const { mutateAsync } = useEsSampleDoc();
  const selectedId = tarsAnalyticsStore.selectedSourceIds[0] ?? null;

  useEffect(() => {
    if (!selectedId) {
      tarsAnalyticsStore.setProjectQuestionKeys([]);
      tarsAnalyticsStore.setProjectQuestionKeysLoading(false);
      tarsAnalyticsStore.setProjectQuestionKeysError(null);
      return;
    }
    const node = tarsStore.getNode(selectedId);
    if (!node || node.type !== "dataRepo") {
      tarsAnalyticsStore.setProjectQuestionKeys([]);
      tarsAnalyticsStore.setProjectQuestionKeysLoading(false);
      tarsAnalyticsStore.setProjectQuestionKeysError(null);
      return;
    }
    const esIndex = node.es_index;
    if (!esIndex) {
      tarsAnalyticsStore.setProjectQuestionKeys([]);
      tarsAnalyticsStore.setProjectQuestionKeysLoading(false);
      tarsAnalyticsStore.setProjectQuestionKeysError(null);
      return;
    }
    let cancelled = false;
    tarsAnalyticsStore.setProjectQuestionKeys([]);
    tarsAnalyticsStore.setProjectQuestionKeysError(null);
    tarsAnalyticsStore.setProjectQuestionKeysLoading(true);
    void (async () => {
      try {
        const raw = await mutateAsync({ es_index: esIndex });
        if (cancelled || raw == null || typeof raw !== "object") {
          if (!cancelled) {
            tarsAnalyticsStore.setProjectQuestionKeysError(
              "Sample document response was empty."
            );
          }
          return;
        }
        const data = raw as Record<string, unknown>;
        const src = data._source ?? data.source;
        if (!src || typeof src !== "object") {
          if (!cancelled) {
            tarsAnalyticsStore.setProjectQuestionKeysError(
              "No document fields in sample response."
            );
          }
          return;
        }
        const keys = extractProjectQuestionKeysFromSource(
          src as Record<string, unknown>
        );
        if (!cancelled) {
          tarsAnalyticsStore.setProjectQuestionKeys([...keys].sort());
          tarsAnalyticsStore.setProjectQuestionKeysError(null);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          tarsAnalyticsStore.setProjectQuestionKeys([]);
          tarsAnalyticsStore.setProjectQuestionKeysError(
            isEsIndexNotFoundError(err)
              ? "This index was not found in Elasticsearch. Survey data may not be indexed yet."
              : extractErrorMessage(err, "Could not load sample document.")
          );
        }
      } finally {
        if (!cancelled) tarsAnalyticsStore.setProjectQuestionKeysLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedId, mutateAsync, tarsStore, tarsAnalyticsStore]);

  return null;
});

export default EsSampleKeysSync;
